import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';

const SelectContext = React.createContext();

export function Select({ value, onValueChange, children }) {
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  return (
    <SelectContext.Provider value={{ value, onValueChange, open, setOpen }}>
      <div ref={selectRef} className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({ children, className, ...props }) {
  const { open, setOpen } = React.useContext(SelectContext);
  
  return (
    <button
      type="button"
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        className
      )}
      onClick={() => setOpen(!open)}
      {...props}
    >
      {children}
      <svg
        className="h-4 w-4 opacity-50"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

export function SelectValue({ placeholder, children, ...props }) {
  const { value } = React.useContext(SelectContext);

  return (
    <span {...props}>
      {children !== undefined && children !== null && children !== ''
        ? children
        : value || placeholder || 'Select...'}
    </span>
  );
}

export function SelectContent({ children, className, ...props }) {
  const { open } = React.useContext(SelectContext);
  
  if (!open) return null;
  
  return (
    <div
      className={cn(
        'absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg',
        'max-h-60 overflow-auto',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SelectItem({ value: itemValue, children, className, ...props }) {
  const { value, onValueChange, setOpen } = React.useContext(SelectContext);
  
  return (
    <div
      className={cn(
        'px-3 py-2 text-sm cursor-pointer hover:bg-slate-100',
        value === itemValue && 'bg-blue-50 text-blue-700',
        className
      )}
      onClick={() => {
        onValueChange?.(itemValue);
        setOpen(false);
      }}
      {...props}
    >
      {children}
    </div>
  );
}

